/* eslint-disable max-len */

import { TPaginationOptions } from '../../../types';

import {
  IBidHistoryQueryOptions,
  IFetchAllStagedBidsReportPayload,
  IFetchStagedBidsPayload,
  IRanksDetailedReportAdminFilterQueryParams,
  TBidModelsFiltersUrlOptions,
  TFetchAllBidsUrlOptions,
  IFetchAllUploadedBidsPayload,
  TFetchUploadBidFilesPayload,
  IFetchBidsUrlOptions,
  IFetchDownloadBidsTemplatePayload,
  IBidValidityConfigurationQueryParams,
} from './types';

/**
 * `bidCategoryFiltersUrl` is a function to generate endpoint for category, brands
 * and rank report filter dropdowns and component.
 */
export const bidCategoryFiltersUrl = (): string =>
  '/category/active-list-brand-bids';

/**
 * `bidModelsFiltersUrl` is a function to generate endpoint for model filter
 * dropdown and data for rank report component.
 */
export const bidModelsFiltersUrl = ({
  categoryId,
  brandId,
}: TBidModelsFiltersUrlOptions) => `/assets/groups/${categoryId}/${brandId}`;

// TODO: Remove this once the `useDownloadBidTemplateQuery` is stable.
// /**
//  * `downloadBidBookUrl` is a function to generate endpoint for downloading bid book.
//  */
// export const downloadBidBookUrl = ({
//   categoryId,
//   brandId,
// }: IDownloadBidBookUrlOptions) =>
//   `/assets/export/active-asset-bids/${categoryId}/${brandId}`;

/**
 * Function to generate endpoint for downloading dealer bid template (excel).
 */
export const downloadDealerBidTemplateUrl = (
  brandId: string,
  categoryId: string
) => `/assets/export/active-asset-bids/${categoryId}/${brandId}`;

export const bidStatusUpdateUrl = (assetId: string) =>
  `/bids/dealer/${assetId}`;

/**
 * Update bid mutation url function
 */
export const updateBidUrl = () => '/bids';

/**
 * TODO: Remove this since it's not being used anywhere.
 * @deprecated
 */
export const fetchAllBidsUrl = ({
  categoryId,
  brandId,
  page,
  count,
}: TFetchAllBidsUrlOptions) => {
  if (brandId === 'all') {
    return `/assets/all-assets-with-bid/${categoryId}?page=${page}&count=${count};`;
  }
  return `/assets/all-assets-with-bid/${categoryId}/${brandId}?page=${page}&count=${count};`;
};

export const fetchAllBidsByModelsUrl = ({ page, count }: TPaginationOptions) =>
  `/assets/with-bid-details-by-groupIds?page=${page}&count=${count};`;

export const fetchBidsQuantityUrl = (assetId: string) =>
  `/bids/bid-options/${assetId}`;

/**
 * `uploadBidBookUrl` is a function to generate endpoint for uploading bid book.
 */
export const uploadBidBookUrl = () => '/assets/import/active-asset-bids';

/**
 * function to generate endpoint for bid list.
 */
export const bidSearchUrl = (urlParams?: IFetchBidsUrlOptions) => {
  const { fromDate, groupIds, isHotDeal, toDate } = urlParams || {};

  if (isHotDeal) {
    return '/assets/top-trending/list';
  }

  const params = new URLSearchParams();

  if (fromDate) params.append('fromDate', fromDate);
  if (toDate) params.append('toDate', toDate);

  if (groupIds?.length) {
    groupIds.forEach((id) => params.append('groupId', id));
  }

  const queryStr = params.toString();

  return `/assets/bid-list?version=v2${queryStr ? `&${queryStr}` : ''}`;
};

/**
 * function to generate endpoint for bid list.
 */
export const bidSearchPaasUrl = () => '/paas/biding';

export const fetchBidHistoryUrl = ({
  assetId,
  count,
  page,
}: IBidHistoryQueryOptions) =>
  `bids/dealer-bid-history/${assetId}?page=${page}&count=${count}`;

export const rankReportUrl = () => '/bids/summary';

export const fetchStepperValueUrl = (assetId: string) =>
  `/bids/stepper-value/${assetId}`;

/**
 * function to generate endpoint for bid item condition.
 */
export const fetchBidItemConditionUrl = (assetId: string) =>
  `/v3/configuration/item-condition/info/${assetId}`;

/**
 * function to generate endpoint for bid item condition.
 */
export const bidRankUrl = () => '/bids/ranks/';

/**
 * function to generate endpoint for bid details.
 */
export const fetchBidDetailsUrl = (assetId: string) =>
  `/assets/asset-details-with-bid-details/${assetId}`;

/**
 * Function to generate endpoint for uploaded bid file list.
 */
export const fetchUploadBidFilesUrl = ({
  categoryId,
  count,
  page,
}: TFetchUploadBidFilesPayload) =>
  `/paas/dealer/bid-upload-files/${categoryId}?count=${count}&page=${page}`;

/**
 * Function to generate endpoint for upload bid file template.
 */
export const downloadUploadBidFileTemplateUrl = (categoryId: string) =>
  `/assets/${categoryId}/export/asset-bid-upload-template`;

/**
 * Function to generate endpoint for bid file upload by category.
 */
export const uploadBidFileUrl = (categoryId: string) =>
  `/paas/dealer/bid-upload/${categoryId}`;

/**
 * function to generate endpoint for uploaded bids list.
 *
 * @param isInstantBiddingEnabled - When true, uses the direct live bidding
 * endpoint. When false, uses the staged bidding (paas) endpoint.
 */
export const uploadedBidSearchUrl = ({
  bidValidityConfigId,
  brandIds,
  categoryId,
  count,
  groupIds,
  isInstantBiddingEnabled,
  page,
  searchText,
  status,
}: IFetchAllUploadedBidsPayload) => {
  const params = new URLSearchParams();

  if (bidValidityConfigId) {
    params.append('bidValidityConfigId', bidValidityConfigId);
  }
  if (categoryId) params.append('categoryId', categoryId);
  if (searchText) params.append('search', searchText);
  if (page) params.append('page', page.toString());
  if (count) params.append('limit', count.toString());
  if (status?.length) {
    status.forEach((s) => params.append('status', s));
  }
  if (brandIds?.length) {
    brandIds.forEach((id) => params.append('brandId', id));
  }
  if (groupIds?.length) {
    groupIds.forEach((id) => params.append('groupId', id));
  }

  const queryStr = params.toString();

  return isInstantBiddingEnabled
    ? `/bids/uploaded-bids-files${queryStr ? `?${queryStr}` : ''}`
    : `/paas/uploaded-bid-files${queryStr ? `?${queryStr}` : ''}`;
};

/**
 * Function to generate endpoint for fetching the bid file template, preferably
 * in the excel format.This file will be used to upload bids.
 */
export const downloadBidFileTemplateUrl = ({
  bidValidityConfigId,
  brandIds,
  categoryId,
  groupIds,
}: IFetchDownloadBidsTemplatePayload) => {
  const baseUrl = `/paas/${categoryId}/bid-template`;
  const params = new URLSearchParams();

  if (bidValidityConfigId) {
    params.append('bidValidityConfigId', bidValidityConfigId);
  }

  if (brandIds && brandIds.length > 0) {
    if (Array.isArray(brandIds)) {
      brandIds.forEach((id) => params.append('brandId', id));
    } else {
      params.append('brandId', brandIds);
    }
  }

  if (groupIds && groupIds.length > 0) {
    groupIds.forEach((id) => params.append('groupId', id));
  }

  const queryStr = params.toString();

  return queryStr ? `${baseUrl}?${queryStr}` : baseUrl;
};

/**
 * Function to generate endpoint for uploading the bid template file.
 */
export const uploadBidTemplateFileUrl = (categoryId: string) =>
  `/paas/${categoryId}/bids`;

/**
 * Url for getting the information about the bidding window
 */
export const fetchBiddingWindowInfoUrl = () => '/paas/dealer/bidding-window';

/**
 * Url for getting the information about the timed auction time period
 */
export const fetchTimedAuctionTimePeriodUrl = () =>
  '/timed-auction/dealer/window';

/**
 * Function to generate endpoint for fetching staged bids data.
 * endpoint -> /paas/staged-bid-data/:categoryId/:bidValidityConfigId?brandId=&groupId=&search=iPhone&page=1&limit=20
 */
export const fetchStagedBidsUrl = ({
  bidValidityConfigId,
  brandIds,
  categoryId,
  groupIds,
  count: limit,
  page,
  search,
  winningStatus,
}: IFetchStagedBidsPayload) => {
  const params = new URLSearchParams();

  if (bidValidityConfigId) {
    params.append('bidValidityConfigId', bidValidityConfigId);
  }
  if (categoryId) params.append('categoryId', categoryId);

  if (brandIds?.length) {
    brandIds.forEach((id) => params.append('brandId', id));
  }
  if (groupIds?.length) {
    groupIds.forEach((id) => params.append('groupId', id));
  }
  if (search) params.append('search', search);
  if (page) params.append('page', page.toString());
  if (limit) params.append('limit', limit.toString());
  if (winningStatus) params.append('winningStatus', winningStatus);

  const queryStr = params.toString();

  return `/paas/staged-bid-data${queryStr ? `?${queryStr}` : ''}`;
};

export const fetchBidValidityConfigurationUrl = ({
  type,
}: IBidValidityConfigurationQueryParams) => {
  const baseUrl = '/paas/dealer/bid-validity-configurations';

  if (type === 'active') {
    return `${baseUrl}?active=true`;
  }

  return baseUrl;
};

/**
 * Function to generate endpoint for fetching bidding windows by bid validity
 * configuration ID.
 */
export const fetchBiddingWindowsByValidityConfigUrl = (
  bidValidityConfigId: string
) =>
  `/paas/dealer/bid-validity-configuration/${bidValidityConfigId}/bidding-windows`;

/**
 * Returns the URL for the ranks detailed report admin endpoint.
 */
export const fetchRanksDetailedReportAdminUrl = (
  filters?: IRanksDetailedReportAdminFilterQueryParams
) => {
  const params = new URLSearchParams();

  if (filters?.brandId) {
    params.append('brandId', filters.brandId);
  }

  if (filters?.categoryId) {
    params.append('categoryId', filters.categoryId);
  }

  const queryStr = params.toString();

  return `/bids/ranks-detailed-report-admin${queryStr ? `?${queryStr}` : ''}`;
};

/**
 * Returns the URL for the store details endpoint.
 */
export const fetchStoreDetailsUrl = (storeId: string) => `/store/${storeId}`;

export const fetchExportBidsExcelUrl = (params?: {
  fromDate?: string;
  toDate?: string;
}) => {
  const baseUrl = '/assets/dealer/export-bidding-data';

  if (!params || (!params.fromDate && !params.toDate)) {
    return baseUrl;
  }

  const urlParams = new URLSearchParams();
  if (params.fromDate) urlParams.append('fromDate', params.fromDate);
  if (params.toDate) urlParams.append('toDate', params.toDate);

  const queryStr = urlParams.toString();
  return queryStr ? `${baseUrl}?${queryStr}` : baseUrl;
};

export const fetchExportDealsExcelUrl = () => '/bids/export-deals';

/**
 * Function to generate endpoint for fetching staged bids report data.
 * Endpoint -> /paas/staged-bids-report?bidValidityConfigId=&categoryId=&search=&page=1&limit=20&status=PENDING&status=PROCESSED
 */
export const stagedBidsReportSearchUrl = ({
  bidValidityConfigId,
  biddingWindowId,
  categoryId,
  count,
  page,
  searchText,
  status,
}: IFetchAllStagedBidsReportPayload) => {
  const params = new URLSearchParams();

  if (bidValidityConfigId) {
    params.append('bidValidityConfigId', bidValidityConfigId);
  }
  if (biddingWindowId) {
    params.append('biddingWindowId', biddingWindowId);
  }
  if (categoryId) params.append('categoryId', categoryId);
  if (searchText) params.append('search', searchText);
  if (page) params.append('page', page.toString());
  if (count) params.append('limit', count.toString());
  if (status?.length) {
    status.forEach((s: string) => params.append('status', s));
  }

  const queryStr = params.toString();

  return `/paas/dealer/staged-bid-reports${queryStr ? `?${queryStr}` : ''}`;
};
